import React from "react";
import { FileText, Plus, Trash2 } from "lucide-react";
import OnboardingSectionCard from "../OnboardingSectionCard";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import CommonInput from "@/shared/components/ui/Input/CommonInput";

export default function CompanyAssetsSection({
  localAssets,
  activeType,
  setActiveType,
  handleAddRow,
  handleDeleteRow,
  handleUpdateRowField,
}) {
  const filteredAssets = localAssets.filter((a) => a.type === activeType);

  return (
    <OnboardingSectionCard
      icon={<FileText className="text-gray-500" size={22} />}
      title="Company Assets"
    >
      <div className="flex flex-col space-y-6">
        {/* Step 1: Select asset type */}
        <div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">1. Select asset type</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                key: "case_study",
                title: "Case study",
                subtitle: "Customer success stories",
              },
              {
                key: "thought_leadership",
                title: "Thought leadership",
                subtitle: "POVs, whitepapers, articles",
              },
              {
                key: "marketing_collateral",
                title: "Marketing collateral",
                subtitle: "Brochures, decks, one-pagers",
              },
            ].map((typeObj) => {
              const isActive = activeType === typeObj.key;
              return (
                <button
                  key={typeObj.key}
                  type="button"
                  onClick={() => setActiveType(typeObj.key)}
                  className={`flex flex-col text-left p-5 rounded-xl border transition-all duration-200 bg-white dark:bg-gray-900 ${isActive
                    ? "border-secondary ring-1 ring-secondary shadow-sm"
                    : "border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700"
                    }`}
                >
                  <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-1">{typeObj.title}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{typeObj.subtitle}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 2: Add clients */}
        <div className="flex flex-col space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">2. Add clients</h3>
            <SecondaryButton
              title="Add row"
              icon={<Plus size={16} className="text-secondary shrink-0" />}
              onClick={handleAddRow}
            />
          </div>

          <div className="overflow-y-auto max-h-[300px] overflow-x-auto w-full border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 relative">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-sm table-fixed">
              <thead className="sticky top-0 z-10 border-b border-gray-200 dark:border-gray-800 bg-[#F9FAFB] dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-300 w-[30%] bg-[#F9FAFB] dark:bg-gray-800">Client name</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-300 w-[40%] bg-[#F9FAFB] dark:bg-gray-800">Description</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-300 w-[15%] bg-[#F9FAFB] dark:bg-gray-800">Attachment (PDF)</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-300 w-[15%] bg-[#F9FAFB] dark:bg-gray-800">Link</th>
                  <th className="px-2 py-3 text-center font-medium text-gray-500 dark:text-gray-300 w-[5%] bg-[#F9FAFB] dark:bg-gray-800"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {filteredAssets.map((row) => {
                  const rowId = row.id || row.fileId;
                  return (
                    <tr key={rowId} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 align-middle">
                        <CommonInput
                          value={row.clientName || ""}
                          onChange={(e) => handleUpdateRowField(rowId, "clientName", e.target.value)}
                          placeholder="Acme Corp"
                          className="min-w-0 w-full px-3 py-2 text-sm"
                          wrapperClassName="min-w-0 w-full"
                        />
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <CommonInput
                          value={row.description || ""}
                          onChange={(e) => handleUpdateRowField(rowId, "description", e.target.value)}
                          placeholder="Short summary of the asset"
                          className="min-w-0 w-full px-3 py-2 text-sm"
                          wrapperClassName="min-w-0 w-full"
                        />
                      </td>
                      <td className="px-4 py-3 align-middle">
                        {row.name ? (
                          <div className="flex items-center justify-between px-3 py-2 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50/50 dark:bg-gray-800/50 gap-2">
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate max-w-[80px]" title={row.name}>
                              {row.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                handleUpdateRowField(rowId, "name", "");
                                handleUpdateRowField(rowId, "file", null);
                              }}
                              className="text-gray-400 hover:text-red-500 text-xs font-semibold focus:outline-none shrink-0"
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <div className="relative">
                            <SecondaryButton
                              title="Upload"
                              icon={<Plus size={16} className="text-secondary shrink-0" />}
                              onClick={() => document.getElementById(`file-input-${rowId}`)?.click()}
                              className="w-full"
                            />
                            <input
                              id={`file-input-${rowId}`}
                              type="file"
                              accept="application/pdf"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  handleUpdateRowField(rowId, "file", file);
                                  handleUpdateRowField(rowId, "name", file.name);
                                }
                              }}
                            />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <CommonInput
                          value={row.link || ""}
                          onChange={(e) => handleUpdateRowField(rowId, "link", e.target.value)}
                          placeholder="https://..."
                          className="min-w-0 w-full px-3 py-2 text-sm"
                          wrapperClassName="min-w-0 w-full"
                        />
                      </td>
                      <td className="px-2 py-3 text-center align-middle">
                        <button
                          type="button"
                          onClick={() => handleDeleteRow(rowId)}
                          className="text-gray-400 hover:text-red-500 p-1.5 rounded-full focus:outline-none transition-colors"
                          title="Delete row"
                        >
                          <Trash2 size={20} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredAssets.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-400 text-xs">
                      No client details added for this asset type yet. Click "Add row" to start.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-gray-400">Attachment and link are optional per row.</p>
        </div>
      </div>
    </OnboardingSectionCard>
  );
}
